/**
 * @typedef QuestionnaireStep
 * @type {Object}
 * @property {String} uid
 * @property {String} name
 * @property {Array<*>} question
 * @property {Array<String>} behaviour
 */

/**
 * @typedef Questionnaire
 * @type {Object}
 * @property {String} name
 * @property {String} uid
 * @property {Array<QuestionnaireStep>} steps
 */


import React from "react";
import _ from 'lodash';
import moment from 'moment'

import {
  getDanteCustomerData,
  getQuestionType,
  getStepAnswer,
  getUID, isValidBirthDate, setContactsOptionalFlag,
  setQuestionAnswer,
  setQuestionAnswerFromConfig, UpdateCustomerAndSync
} from "./utils";
import {QuestionnairesHandlerResource} from "../../utils/api";
import {congratsStep, CustomerDataMapping, finalStep, riskProfileSelectionStep} from "./mock";
import {
  CONGRATS_STEP_ID,
  CONFIRMATION_STATE,
  COUPLE_MEMBERS_CONFIRMATION_STEP_IDENTIFIERS, COUPLE_MEMBERS_DISABLED_FIELDS_IDENTIFIERS,
  QUESTION_TYPE, SECURITY_DOCUMENT_HANDED_STEP_UIDS,
  stepGroups,
  USER_CONFIRMATION_STEP_IDENTIFIER,
  RISK_PROFILING_USER_STEPS, LIABILITY_DATA_UID, LIABILITY_CHECKBOX_UID, LIABILITIES_V2_STEP_UID, radioYesUID,
  radioSelectNoKnowledgeValue, radioSelectNoExperienceValue, radioNoUID,
} from "./constants";
import {Service} from "./service";
import InvestmentIntermediaryInformation
  from "./components/StepContent/components/step/InvestmentIntermediaryInformationStep";
import {getOriginLink} from '../../utils/constants'
import {displaySuccessSnackBar, displayWarningSnackBar} from "../../components/SnackbarProvider/actions";
import {SERVICE_CONCEPTS} from "../Trades/constants";

/**
 * Validate, if step is Customer validation step.
 * @param {QuestionnaireStep} step - Step data
 * @return {Boolean} - Result of validation
 * */
const isUserConfirmationStep = (step) => getUID(step.uid) === USER_CONFIRMATION_STEP_IDENTIFIER

/**
 * Validate, if step is Customer validation step.
 * @param {QuestionnaireStep} step - Step data
 * @return {Boolean} - Result of validation
 * */
const isUserStep = (step) => RISK_PROFILING_USER_STEPS.includes(getUID(step.uid));


/**
 * Validate, if step is Couple member validation step.
 * @param {QuestionnaireStep} step - Step data
 * @return {Boolean} - Result of validation
 * */
const isCoupleMemberConfirmationStep = (step) => COUPLE_MEMBERS_CONFIRMATION_STEP_IDENTIFIERS.includes(getUID(step.uid))

/**
 * Validate, if step is risk profile data validation step.
 * @param {QuestionnaireStep} step - Step data
 * @return {Boolean} - Result of validation
 * */
const isRiskProfileStep = (step) => !isUserConfirmationStep(step) && !isCoupleMemberConfirmationStep(step)

/**
 * Get step number from step UID.
 * @param {QuestionnaireStep} step - Step data
 * @return {Number} - Step UID number
 * */
const getStepNumberFromUID = (step) => {
  const customAnlageberatungIndexes = {
    "21": 3.4, // in the new process A2 is splitted into steps: A21, A2, A22
    "2": 3.5, // put A2 after A3
    "22": 3.6,
    "23": 3.7,
    "13": 9.5, // put A13 before A10
    "71": 7.1, // put A71 after A7
    "72": 7.2, // put A72 after A71
  };

  const customAnlagevermittlungIndexes = {
    "21": 2.4, // in the new process K2 is splitted into 3 steps: K21, K2, K22
    "1": 2.5, // put K1 after K2
    "22": 2.6,
    "23": 2.7,
  };

  let customIndexes = {};

  // we have "A" step naming ONLY for Anlageberatung
  // for Anlagevermittlung steps starts with "K"
  switch (step.uid.charAt(0)) {
    case "A":
      customIndexes = customAnlageberatungIndexes;
      break;
    case "K":
      customIndexes = customAnlagevermittlungIndexes;
      break;
  }

  let stepIndex = parseInt(step.uid.substring(1));  // remove first char

  return _.get(customIndexes, `${stepIndex}`, stepIndex || 0); // 0 = all equal = keep order
};

/**
 * Check if UID is valid couple member step identifier and return member index.
 * @param {String} uid - Step UID
 * @return {Number} - Member index if UID is valid
 * */
export const getMemberIndexFromStepUID = (uid) => {
  if (!!uid && uid.match(/.*0[1,2]$/)) {
    return parseInt(uid.substr(-1)) - 1
  }
}

const moveGroupQuestions = (step, group, groups) => {
  const groupItems = _.remove(step.question, (q) => group.questionUIDs.includes(getUID(q.uid)));
  if(!_.isEmpty(groupItems)) {
    // use info text from first question with info
    const infoTextItem = groupItems.find(i => i.info);
    // add all questions to groups
    groups.push({...group, question: groupItems, info_text: infoTextItem && infoTextItem.info});
  }
}


export class InteractService extends Service {
  constructor(uid, name, next_btn, success_text, getPrevStepAnswer, onLegalDocumentsButtonClick) {
    super(uid, name, next_btn, success_text, getPrevStepAnswer);
    this.__is_customer_confirmation = this.uid === 'user_confirmation';
    this._use_session = !this.__is_customer_confirmation;
    this._session_id = null;  // Interact session UID
    this._finalStep = this.__deepCopy(finalStep);
    this._defaultRiskProfileToken = null; // Dante RiskProfile UID to pre-fill answers from
    this._riskProfilesSelectionStep = this.__deepCopy(riskProfileSelectionStep);
    this._riskProfiles = [];
    this.__isCustomAnswers = false; // flag to get step / question UID
    this.is_investment_questionnaire = false;
    this.onLegalDocumentsButtonClick = onLegalDocumentsButtonClick;
    this.srri = null; // show SRRI step to confirm
  }

  get hideSideMenu() {
    return this.step && this.step === this._riskProfilesSelectionStep
  }

  /**
   * Make questions from step as disabled using provided identifiers.
   * @param {QuestionnaireStep} step - Step
   * @param {Array<String>} identifiers - Identifiers, that should be disabled
   * */
  __makeQuestionsDisabled(step, identifiers) {
    step.question.forEach(question => {
      if (identifiers.includes(getUID(question.uid))) {
        question.disabled = true;
        question.optional = true
      }
    })
  }

  /**
   * Prepare Interact questionnaire.
   * @param {Questionnaire} questionnaire - Questionnaire data.
   * @return {Questionnaire} - Questionnaire with valid steps configuration
   * */
  __buildQuestionnaire(questionnaire) {

    const { steps } = questionnaire;
    const stepsSorted = _.sortBy(steps, getStepNumberFromUID);

    const userConfirmationStep = _.filter(stepsSorted, isUserConfirmationStep)
    if (userConfirmationStep.length) {
      this.__makeQuestionsDisabled(userConfirmationStep[0], COUPLE_MEMBERS_DISABLED_FIELDS_IDENTIFIERS)
    }
    const coupleMembersConfirmationSteps = _.filter(stepsSorted, isCoupleMemberConfirmationStep)
    const otherRiskProfileDataSteps = _.filter(stepsSorted, isRiskProfileStep)

    const stepsConfigured = _.flatten(
      [userConfirmationStep, coupleMembersConfirmationSteps, this.__is_customer_confirmation ? [] : otherRiskProfileDataSteps]);


    const KNOWLEDGE_GROUP = 'Kenntnisse & Erfahrungen';
    const ASSETS_GROUP = 'Vermögen';
    const stepsParents = {
      'A21': KNOWLEDGE_GROUP, 'A22': KNOWLEDGE_GROUP, 'A2': KNOWLEDGE_GROUP,
      'A7': ASSETS_GROUP, [LIABILITIES_V2_STEP_UID]: ASSETS_GROUP, 'A72': ASSETS_GROUP,
    };

    stepsConfigured.forEach((step, idx) => {
      if(this.isNewDesign) {
        const stepUID = this._getStepUID(step.uid);
        step.parent = stepsParents[stepUID];
        // FIXME: need to apply workaround
        step.not_skippable = idx === 0;  // first step can't be skipped, restriction on Interact (don't know why)
        step.question.forEach(q => {
          if (q.question_text === '{EMPTY}') {
            q.question_text = undefined;
          }
          // Merge only defined props into question
          _.assign(q, _.pick(q.extra, ['custom_classes', 'size', 'required_check', 'row']));
        })
      }

      // Merge only defined props into step
      _.assign(step, _.pick(step.extra, ['info_text', 'category', 'custom_classes', 'extra_texts', 'groups']));
    })

    return {
      ...questionnaire,
      steps: stepsConfigured
    }
  }

  /**
   * Fill questions with customer data.
   * @param {function} getUserDataFunction - Async function to get user data
   * @param {function} getUserStepFunction - Function, that should be passed to iterator, to find needed step
   * @param {function} setQuestionAnswerFunction - Function, that handle question answer handling
   * @param {function} [handleUserDataCallback] - Callback, that should be called with user data
   * */
  async __prefillUserData(getUserDataFunction, getUserStepFunction, setQuestionAnswerFunction, handleUserDataCallback) {

    if (!this._questionnaire) return

    const userSteps = _.filter(this._questionnaire.steps, getUserStepFunction);

    if (userSteps.length) {

      const userData = await getUserDataFunction(this._customer_id);
      handleUserDataCallback && handleUserDataCallback(userData);

      userSteps.forEach(step => {
        (step.question || []).map((question) => {
          this.__checkReadOnlyQuestion(question);
          setQuestionAnswerFunction(question, userData)
        });
      })
    }

  }

  /**
   * Fill questions with couple members data.
   * @param {Array<Object>} membersData - Couple members data
   * @param {String} stepUID - UID of step, that should be prefilled with data
   * */
  async __prefillMembersData(membersData, stepUID) {

    const isValidQuestionnaire = !!this._questionnaire
    const isValidMembers = this.useMembers && Array.isArray(this.members) && this.members.length

    if (!isValidQuestionnaire || !isValidMembers) return

    membersData.forEach((member, index) => {
      const memberStepUID = `${stepUID}0${index + 1}`
      this.__prefillUserData(
        async () => member,
        (step) => getUID(step.uid) === memberStepUID,
        (question, userData) => {
          setQuestionAnswerFromConfig(question, userData, this.__mappingConfFunction);
        })
    })

  }

  _syncQuestions() {
    if (!this._questionnaire) return
    this._questionnaire.steps.forEach(step => {
      if (getUID(step.uid) === USER_CONFIRMATION_STEP_IDENTIFIER) {
        step.question.forEach(question => {
          if (getUID(question.uid) === 'A13') {
            question.onButtonClick = this.onLegalDocumentsButtonClick
          }
        })
      }
    })
  }

  async updateCustomerMemberData() {
    const memberIndex = getMemberIndexFromStepUID(getUID(this._step.uid))
    if (!_.isUndefined(memberIndex) && this.members[memberIndex]) {

      let customerData = await UpdateCustomerAndSync(this.members[memberIndex].id, this._step, this.__mappingConfFunction, undefined, this.isSwitchSellFlow);
      // TODO: As an improvement for future we should update first step in case related member data was updated
      if (customerData) {
        let { data } = customerData
        this.members[memberIndex] = data

        let updatedData = {
          "relationships": this.members
        }

        await this.__prefillUserData(
          async () => updatedData,
          isUserConfirmationStep,
          (question, userData) => {
            setQuestionAnswerFromConfig(question, userData, this.__mappingConfFunction);
          })
        let userConfirmationStep = _.find(this._questionnaire.steps, isUserConfirmationStep)
        if (!!userConfirmationStep) {
          await this.__sendStepAnswerData(userConfirmationStep)
        }
      }
    }
  }

  // flag to identify if we need to use existing Risk Profile
  get useSRRI() {
    return !!(this.srri && this._session_id);
  }

  get isFistStep() {
    return this.useSRRI || super.isFistStep;
  }

  get hideProgress(){
    return this._step && this._step.uid === this._riskProfilesSelectionStep.uid;
  }

  /**
   * Make Security Documents question required.
   *
   * @example
   * For sell only trading questions should be optional.
   * For other than that - function should be executed.
   *
   * @private
   */
  __makeSecurityDocumentHandedQuestionRequired() {

    this._questionnaire.steps.forEach((step) => {

      if (!SECURITY_DOCUMENT_HANDED_STEP_UIDS.includes(getUID(step.uid))) return;

      step.question.forEach((question) => {

        if (getUID(question.uid) !== 'I1') return;

        question.optional = false
      })

    })

  }

    /**
   * Clean question answers for specific step.
   * Tested with SECURITY_DOCUMENT_HANDED_STEP_UIDS steps.
   * Pay attention when use with other steps.
   *
   * @param step - Step
   * @private
   */
  cleanStepAnswers(step) {
    step.question.forEach((question) => {
      question.answer = [];
    })
  }

  async __setQuestionnaire(onboardingAnswers, initOnly) {
    await super.__setQuestionnaire(onboardingAnswers, initOnly);

    if(!this.isNewDesign){
      this._riskProfilesSelectionStep.custom_classes = {"stepName": "text-left"};
    }

    let response = await QuestionnairesHandlerResource.get({
      customer_id: this._customer_id, is_investment_questionnaire: this.is_investment_questionnaire, new_design: this.__is_new_design, with_new_other_assets: this.isWithNewOtherAssets});
    const fullAdvisory = !this.is_investment_questionnaire && !this.__is_customer_confirmation;
    let answers = [];

    this._questionnaire = this.__buildQuestionnaire(response.response);
    // init _finalStep as completed if sri is stored in answers
    if(initOnly && !this._finalStep.confirmationState){
      this._finalStep.confirmationState = _.get(onboardingAnswers, 'srri')
        ? CONFIRMATION_STATE.CONFIRMED
        : CONFIRMATION_STATE.SKIPPED;
    }
    if (this.useSRRI) {
      try {
        response = await QuestionnairesHandlerResource.at('get_score/').get({session: this._session_id});
        answers = response && response.response && response.response.answers || [];
        this.__setFinalQuestionData(this.srri, this.srri, true);
        this._step = this._finalStep;
      } catch (e) {
        this.warning = "Bei der Datenabfrage für das aktuelle Risikoprofil ist ein Fehler aufgetreten.";
        console.error("ERROR DURING GET EXISTING RISK PROFILE");

        this.__initVars();
        this.srri = null;
        this._use_session = false;
        await this.__setQuestionnaire(onboardingAnswers, initOnly);
        return;
      }
    } else {
      if(this._use_session && !(initOnly && this._session_id)) {  // skip step set for session init only
        response = await QuestionnairesHandlerResource.at('get_step/').get({
          customer_id: this._customer_id,
          onboarding_uid: this.onboarding_uid,
          is_investment_questionnaire: this.is_investment_questionnaire,
          new_design: this.__is_new_design,
          with_new_other_assets: this.isWithNewOtherAssets
        });
        const res = response && response.response;
        this._defaultRiskProfileToken = response && response.origin_session_id;
        this._session_id = res.session;
        if(!_.isEmpty(res.current_step)) this._step = res.current_step;
      }

      if(this._session_id){
        response = await QuestionnairesHandlerResource.at('get_score/').get({session: this._session_id});
        answers = response && response.response && response.response.answers || [];
        if(fullAdvisory && response && response.response && response.response.completed) {
          // init score from Onboarding or Interact
          this.__initScore(response, onboardingAnswers);
          this._step = this._finalStep;
        }
      } else if(fullAdvisory) {
        // Add Risk Questionnaire Selection for New process
        try {
          this._riskProfiles = await QuestionnairesHandlerResource.at('get-risk-profiles/').get({customer_id: this._customer_id}) || [];
        } catch (e) {
          console.error(e);
        }
        this._questionnaire.steps.unshift(this._riskProfilesSelectionStep);
        this._step = this._riskProfilesSelectionStep;
      }
    }

    if(fullAdvisory){
      // add custom steps
      if(this.is_couple){
        this._finalStep.info = "Für diese Gemeinschaft wird eine {risk_category} Anlagestrategie empfohlen.";
      }
      this._questionnaire.steps.push(this._finalStep);
    }

    // region Fill user confirmation steps with data (and couple members data in case couple onboarding)
    await this.__prefillUserData(
      async (customerId) => this.current_customer || getDanteCustomerData(customerId),
      (step) => isUserStep(step),
      (question, userData) => {
        setQuestionAnswerFromConfig(question, userData, this.__mappingConfFunction);
      },
      (customerData) => {
        this.customer_type = customerData.customer_type;
        this.current_customer = customerData;
        this.members = customerData.relationships;
      });
    await this.__prefillMembersData(this.members, USER_CONFIRMATION_STEP_IDENTIFIER)
    // endregion

    // set answers from our db or questions config
    this._setDefaultAnswers(onboardingAnswers);

    // get Default answers to pre-fill Questionnaire
    let defaultAnswers = await this._getDefaultAnswers();

    let liabilitiesQ, noLiabilitiesCheckboxQ;

    // Compute current step index for confirmationState: steps before it with no answers were skipped.
    const currentStepIdx = this._step
      ? this._questionnaire.steps.findIndex(s => s.uid === this._step.uid)
      : -1;

    this._questionnaire.steps.map((step, idx) => {
      const stepUID = getUID(step.uid);
      let hasStoredAnswers = false;

      (step.question || []).map((q) => {
        const qUID = getUID(q.uid);
        const qAnswers = answers.find(a => qUID === getUID(a.question_uid));

        if(qAnswers){
          hasStoredAnswers = true
        }

        const answer = qAnswers || defaultAnswers.find(a => qUID === getUID(a.question_uid))

        if(answer){
          setQuestionAnswer(q, _.flatten((answer.answer_data && answer.answer_data.answer || []).map(
              answer => _.isObject(answer) && answer.uid ? answer.uid : answer)));
        }

        // Slider/ChartSelect: set step info_text from question info
        if([QUESTION_TYPE.SLIDER, QUESTION_TYPE.CHART_SELECT].includes(getQuestionType(q))){
          step.info_text = q.info;
        }
        // added custom validation for Birth Date
        const customer_type = this.customer_type;
        if(['C1', 'C101', 'C102'].includes(qUID)){
          q.isValid = function(){
            return isValidBirthDate(this, customer_type);
          }
        }

        if(stepUID === LIABILITIES_V2_STEP_UID) {
          if (qUID === LIABILITY_DATA_UID) {
            liabilitiesQ = q;
          } else if (qUID === LIABILITY_CHECKBOX_UID) {
            noLiabilitiesCheckboxQ = q;
          }
        }
      });

      if(this.isNewDesign) {
        // Set confirmationState for Interact steps based on stored answers from the session.
        if (hasStoredAnswers) {
          step.confirmationState = CONFIRMATION_STATE.CONFIRMED;
        } else if (idx < currentStepIdx) {
          step.confirmationState = CONFIRMATION_STATE.SKIPPED;
        }
      }

      let groups = [];

      if(SECURITY_DOCUMENT_HANDED_STEP_UIDS.includes(stepUID)){
        step.step_content = InvestmentIntermediaryInformation;
      }

      // iterate over groups
      if(this.isNewDesign){
        (step.groups || []).forEach(group => {
          moveGroupQuestions(step, group, groups);
        })
      } else {
        Object.entries(stepGroups).map(([key, value]) => {
          if (stepUID === key) {
            value.map((group) => {
              if(this.is_couple && stepUID === 'A1' && group.uid === 'A-address'){
                group.question_text = 'Versandanschrift';
              }
              this.__checkReadOnlyQuestion(group);
              moveGroupQuestions(step, group, groups);
            })
          }
        });
      }
      // add groups to step questions
      step.question = [...groups, ...step.question];
    });

    if(liabilitiesQ && noLiabilitiesCheckboxQ){
      this.setLiabilityCheckboxRequired(noLiabilitiesCheckboxQ, _.isEmpty(liabilitiesQ.answer))
    }

    // set contacts Phone / E-Mail required if it's not SwitchSellFlow
    // NOTE: do it after grouping questions - so "A-contacts" group created
    if (!this.isSwitchSellFlow) {
      setContactsOptionalFlag(this._questionnaire.steps, false);
    }

    let questionnaire;
    try {
      let lastRiskProfile = await QuestionnairesHandlerResource.getLastFinishedRiskProfile(this.customer_id, true, true)
      questionnaire = lastRiskProfile && lastRiskProfile.questionnaire;
    } catch (e) {
      console.error(e);
    }

    if (questionnaire) {
      let { response: { answers } } = questionnaire

      let basicInformationAnswer = _.find(answers, (answer => getUID(answer.question_uid) === 'I1'))
      if (basicInformationAnswer) {
        let { answer_data: { answer }} = basicInformationAnswer

        if (answer && answer.length) {
          let { information_erfolgte_am, versionsnr } = answer[0];

          let basicInformationStepIndex = this._questionnaire.steps.findIndex((step => ['K2', 'A3'].includes(getUID(step.uid))))
          if (basicInformationStepIndex >= 0) {
            let basicInformationQuestionIndex = this._questionnaire.steps[basicInformationStepIndex].question.findIndex(question => getUID(question.uid) === 'I1')
            if (basicInformationQuestionIndex >= 0) {
              this._questionnaire.steps[basicInformationStepIndex].question[basicInformationQuestionIndex].answer = {
                calendar: moment(information_erfolgte_am),
                input: versionsnr,
                checkbox: true
              }
            }
          }
        }
      }
    }

    if(fullAdvisory && this.isNewDesign && this.hasMissingOrSkipped()){
      // in case Risk profile has missing answers clear final and suggested scores/sri
      this.__setSRRIData(undefined, undefined, true);
    }
    if(!this.isNewDesign && !!this.next_btn && !!this.success_body) {
      this._questionnaire.steps.push({...this.__deepCopy(congratsStep), name: this.name});
    }

    if (!this.isSellOnlyFlow) {
      this.__makeSecurityDocumentHandedQuestionRequired()
    } else if (this.step && SECURITY_DOCUMENT_HANDED_STEP_UIDS.includes(getUID(this.step.uid))) {

      this.cleanStepAnswers(this.step)

      await this.nextStep()
    }

  }

  get riskProfiles() {
    return this._riskProfiles;
  }

  hasMissingOrSkipped() {
    return this.steps.some(step =>
      step.uid !== this._finalStep.uid && (
        step.confirmationState === CONFIRMATION_STATE.SKIPPED ||
        (!!step.confirmationState && this.checkHasMissingAnswers(step))
      )
    );
  }

  async resendResult() {
    this.__initVars();
    this.srri = null;
    this._use_session = false;
    if(this.isNewDesign){
      this.__setSRRIData(undefined, undefined, true)
    }
    await this.initQuestionnaire();
  }

  async confirmResult(data) {
    if(this.step.uid === CONGRATS_STEP_ID){
      this._is_finished = true;
      console.log('Thank you');
    } else {
      if (this.useSRRI) {
        // set existing questionnaire to current process
        await QuestionnairesHandlerResource.at('set_onboarding_questionnaire/').post({
          customer_id: this.customer_id,
          session_id: this._session_id,
          onboarding_uid: this.__onboarding_uid,
        })
      // no profile handling for new design
      } else if(!this.isNewDesign || _.get(data, 'srri')) {
        const stepAnswer = data || {};
        stepAnswer.session_id = this._session_id;
        stepAnswer.onboarding_uid = this.onboarding_uid;
        await QuestionnairesHandlerResource.at('set_risk_profile/').post(stepAnswer);
      }

      this.__setStepConfirmed(this.step)
      await this.goNext();
    }
  }

  async nextStep(onlySendData=false) {
    const currentStepUID = this._step.uid;
    if(currentStepUID === this._riskProfilesSelectionStep.uid){
      // get origin session
      const riskProfileToken = this._step.question[0].answer === 'select' && this._step.question[1].answer;
      if(riskProfileToken) {
        this._defaultRiskProfileToken = riskProfileToken;
        // get origin session answers
        const answers = await this._getDefaultAnswers();
        // set origin session answers
        this._questionnaire.steps.map((step) => {
          this._setAnswersFromPrevSession(step, answers);
        })
      }

      this._questionnaire.steps.splice(0, 1); // remove Risk selection step from steps
      await this.__setStep(null, 0);
      return;
    }
    const latestOnboardingData = await this.__sendStepData();
    await this.goNext(onlySendData);

    return latestOnboardingData
  }

  async goNext(onlySendData=false){
    if(this.isLastStep) {
      this._is_finished = true;
    } else {
      if (!onlySendData) {
        await this.__setStep(1);
      }
    }
  }

  _afterStepChange(){
    if(this.isNewDesign){
      const stepUID = this.serviceConcept === SERVICE_CONCEPTS.Anlagevermittlung ? 'K1' : 'A2';
      if(this._getStepUID(this.step.uid) === stepUID){
        this.step.question.forEach(question => {
          if(!question.answer) {
            question.answer = this.getDefaultKnowledgeAnswers(question)
          }
        })
      }
    }
  }

  getDefaultKnowledgeAnswers(question){

    const answersGetters = {
      "H1": "1",
      "H3": "2",
      "H2": "3",
      "H4": "4",
      "H5": "5",
      "H6": "6",
    };

    const checkboxUID = answersGetters[getUID(question.uid)];
    if(checkboxUID){
      const [investmentUID, advisoryUID] = this.isNewDesign ? ['K21', 'A21'] : ['K2', 'A3'];
      // TODO: refactor after full migrate to new design
      const stepUID = this.serviceConcept === SERVICE_CONCEPTS.Anlagevermittlung ? investmentUID : advisoryUID;
      const prevQuestion = this.getQuestion(stepUID, 'I3');

      if (prevQuestion && _.isArray(prevQuestion.answer) && prevQuestion.answer.includes(checkboxUID)) {
        return {
          radio: radioYesUID,
          period_select: radioSelectNoKnowledgeValue,
          order_select: radioSelectNoExperienceValue
        };
      } else if(this.isNewDesign){
        return {
          radio: radioNoUID,
          period_select: radioSelectNoKnowledgeValue,
          order_select: radioSelectNoExperienceValue
        }
      }
    }
  }

  get _successLink(){
    return <a href={getOriginLink(`/#/Kunden/${this._customer_id}/Dokumente`)} target="_blank">Dokumentenablage</a>
  }

  get success_body() {
    // Stammdaten
    if(this.uid === 'user_confirmation') {
      return null;
    }

    // Kenntnisse & Erfahrungen
    if(this.is_investment_questionnaire) {
      return "Die Kenntnisse & Erfahrungen des Kundens wurden erfolgreich gespeichert."
    }

    // RiskProfiling
    return (
      <>
        Das Risikoprofil {this.is_couple ? 'der Familie' : 'des Kunden'}  wurde erfolgreich gespeichert. Sie finden das
        zugehörige Dokument in der {this._successLink} Ihres Kunden.
      </>
    )
  }

  setLiabilityCheckboxRequired(checkbox, required) {
    if(this.isNewDesign){
      checkbox.required_check = required;
    } else {
      checkbox.optional = !required;
    }
  };

  getFinishSnackbarAction(){
    // Stammdaten OR Kenntnisse & Erfahrungen
    if(this.uid === 'user_confirmation' || this.is_investment_questionnaire) {
      return null;
    }

    // RiskProfiling
    return _.isNil(_.get(this.__getFinalQuestion(), 'answer'))
      ? displayWarningSnackBar('Risikoprofil unvollständig: Bitte füllen Sie alle Pflichtfelder aus, um die Erstellung abzuschließen.')
      : displaySuccessSnackBar(
        <>
          Das Risikoprofil {this.is_couple ? 'der Gemeinschaft' : 'des Kunden'} wurde erfolgreich erstellt. Sie finden die
          zugehörigen Informationen auch in der&nbsp;{this._successLink}&nbsp;Ihre{this.is_couple ? 'r' : 's'} Kunden.
        </>,
        30
      )
  }

  async _getDefaultAnswers() {
    if(this._defaultRiskProfileToken) {
      let res = await QuestionnairesHandlerResource.at(
        'get-risk-profile-details/').get({token: this._defaultRiskProfileToken});
      return res && res.response && res.response.answers || [];
    }

    return [];
  }

  _setAnswersFromPrevSession(step, answers){
    (step.question || []).map((q) => {
      if(q.question) {
        this._setAnswersFromPrevSession(q, answers)
      } else {
        // if CustomerData already pre-filled - DO NOT set it from "old" session
        if(_.isNil(q.answer) || !Object.keys(CustomerDataMapping).includes(this._getQuestionUID(q.uid))) {
          this.__setAnswer(q, answers)
        }
      }
    })
  }

  _storeSkippedStep(step_uid) {
    // do nothing
  }

  __setAnswer(q, answers){
    const answer = answers.find(a => getUID(q.uid) === getUID(a.question_uid));
    if (answer) {
      setQuestionAnswer(q, _.flatten((answer.answer_data && answer.answer_data.answer || []).map(
        answer => _.isObject(answer) && answer.uid ? answer.uid : answer)));
    }
  }

  async __sendStepAnswerData(step) {
    const stepAnswer = getStepAnswer(step);
    if (this._session_id) stepAnswer.session = this._session_id;
    if(this._defaultRiskProfileToken) stepAnswer.origin_session_id = this._defaultRiskProfileToken;
    stepAnswer.customer_id = this._customer_id;
    stepAnswer.onboarding_step_uid = this.__onboarding_step_uid;
    stepAnswer.onboarding_uid = this.__onboarding_uid;
    stepAnswer.is_investment_questionnaire = this.is_investment_questionnaire;
    stepAnswer.new_design = this.__is_new_design;
    stepAnswer.with_new_other_assets = this.isWithNewOtherAssets;
    const res = await QuestionnairesHandlerResource.post(stepAnswer);
    this.__setStepConfirmed(step);

    // clear final step confirmation state whenever answers changes
    if(this._finalStep.confirmationState === CONFIRMATION_STATE.CONFIRMED){
      this._finalStep.confirmationState = undefined;
    }

    return res;
  }

  async __sendStepData() {
    if(this._step && isUserStep(this._step)){
      let customerData = await UpdateCustomerAndSync(this._customer_id, this._step, this.__mappingConfFunction, undefined, this.isSwitchSellFlow);

      if (getUID(this._step.uid) === USER_CONFIRMATION_STEP_IDENTIFIER) {
        if (customerData && !customerData.errors && Array.isArray(customerData.relationships) && customerData.relationships.length) {
          let membersDataUpdated = customerData.relationships.map(member => member.data)
          await this.__prefillMembersData(membersDataUpdated, USER_CONFIRMATION_STEP_IDENTIFIER)
        }
      }
    }

    const isValidMembers = this.useMembers && Array.isArray(this.members) && this.members.length

    if (isValidMembers && COUPLE_MEMBERS_CONFIRMATION_STEP_IDENTIFIERS.includes(getUID(this._step.uid))) {
      await this.updateCustomerMemberData()
    }

    let response = await this.__sendStepAnswerData(this._step)
    if(this.__is_customer_confirmation) return response;

    this._session_id = response.response.session;

    const _res = response.response;
    if (_res.current_step && _res.current_step.uid === this._step.uid && !_res.finished){
      throw Error('Invalid step');
    }
    if(!this.is_investment_questionnaire){
      if(this.isNewDesign){
        if(!this.hasMissingOrSkipped()) {
          if(!_res.finished){
            // interact calculate score only after save last step. Note: in our list last step is dummy (final score)
            await this.__sendStepAnswerData(this._questionnaire.steps[this._questionnaire.steps.length - 2])
          }
          await this.__getScore()
        } else {
          this.__setSRRIData(undefined, undefined, true)
        }
      } else if(_res.finished){
        await this.__getScore()
      }
    }
  }

  __initScore(response, defaultAnswers) {
    const final_srri = defaultAnswers && defaultAnswers.srri;
    const suggested_srri = response.response && response.response.band_score_id;
    this.__setFinalQuestionData(suggested_srri, final_srri)
  }

  __setFinalQuestionData(suggested_srri, final_srri, disabled=false) {
    if (suggested_srri || final_srri) {
      this.__setSRRIData(suggested_srri, final_srri, disabled)
    } else if (!this.isNewDesign) {
      throw Error('Can\'t get score');
    }
  }

  __getFinalQuestion(){
    return this._finalStep && _.isArray(this._finalStep.question) && this._finalStep.question[0];
  }

  async __getScore(){
    this.__initScore(await QuestionnairesHandlerResource.at('get_score/').get({session: this._session_id}));
  }

  __setSRRIData(suggested_srri, final_srri, disabled){
    const finalQuestion = this.__getFinalQuestion();
    if(finalQuestion) {
      finalQuestion.answer = final_srri || suggested_srri;
      finalQuestion.value = suggested_srri || final_srri;
      finalQuestion.disabled = !!disabled;
    }
  }

  __mappingConfFunction = (i) => CustomerDataMapping[getUID(i.uid)];

}